import SHA256 from 'crypto-js/sha256';

const FP_STORAGE_KEY = 'alleat_fp';
const FP_READY_KEY = 'alleat_fp_ready';
const SIGNALS_KEY = 'alleat_fp_signals';

const RSA_MAX_PLAIN_BYTES = 53;

let initPromise = null;

function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = '#069';
    ctx.fillText('alleat-fp', 2, 15);
    return canvas.toDataURL();
  } catch (e) {
    return '';
  }
}

function getWebglFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '';
    return [
      gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
      gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
    ].join('|');
  } catch (e) {
    return '';
  }
}

function collectFingerprintPayload(signals) {
  const parts = [
    signals.userAgent,
    signals.language,
    signals.languages,
    signals.platform,
    String(signals.cores),
    `${signals.screenWidth}x${signals.screenHeight}x${signals.colorDepth}x${signals.pixelDepth}`,
    String(signals.timezone),
    signals.canvasRaw,
    signals.webglRaw,
  ];
  return parts.join('||');
}

function encodePlatform(platform) {
  const p = (platform || '').toLowerCase();
  if (p.includes('win')) return '0';
  if (p.includes('mac')) return '1';
  if (p.includes('iphone') || p.includes('ipad')) return '2';
  if (p.includes('android')) return '3';
  if (p.includes('linux')) return '4';
  return '5';
}

const PLATFORM_LABELS = {
  '0': 'Windows',
  '1': 'Mac',
  '2': 'iOS',
  '3': 'Android',
  '4': 'Linux',
  '5': 'Other',
};

function encodeScreen(width, height, colorDepth) {
  const w2 = width.toString(36).slice(-2).padStart(2, '0');
  const h2 = height.toString(36).slice(-2).padStart(2, '0');
  const d1 = colorDepth.toString(36).slice(-1);
  return `${w2}${h2}${d1}`;
}

function decodeScreen(s5) {
  return {
    screenWidth: parseInt(s5.slice(0, 2), 36) || 0,
    screenHeight: parseInt(s5.slice(2, 4), 36) || 0,
    colorDepth: parseInt(s5.slice(4, 5), 36) || 0,
  };
}

function buildDeviceSignals() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const scr = typeof screen !== 'undefined' ? screen : {};
  const canvasRaw = getCanvasFingerprint();
  const webglRaw = getWebglFingerprint();
  const userAgent = nav.userAgent || '';
  const language = nav.language || '';
  const languages = (nav.languages || []).join(',');
  const platform = nav.platform || '';
  const cores = Number(nav.hardwareConcurrency) || 0;
  const screenWidth = scr.width || 0;
  const screenHeight = scr.height || 0;
  const colorDepth = scr.colorDepth || 0;
  const pixelDepth = scr.pixelDepth || 0;
  const timezone = new Date().getTimezoneOffset();
  const platformCode = encodePlatform(platform);

  const payload = collectFingerprintPayload({
    userAgent,
    language,
    languages,
    platform,
    cores,
    screenWidth,
    screenHeight,
    colorDepth,
    pixelDepth,
    timezone,
    canvasRaw,
    webglRaw,
  });
  const fullHash = SHA256(payload).toString();

  return {
    fp8: fullHash.slice(0, 8),
    ua4: SHA256(userAgent).toString().slice(0, 4),
    cv4: SHA256(canvasRaw).toString().slice(0, 4),
    wg4: SHA256(webglRaw).toString().slice(0, 4),
    timezone,
    screenWidth,
    screenHeight,
    colorDepth,
    pixelDepth,
    cores,
    platform,
    platformCode,
    platformLabel: PLATFORM_LABELS[platformCode] || 'Other',
    language: language.slice(0, 2).toLowerCase(),
    languages,
    userAgent,
    canvasRaw,
    webglRaw,
    screenCode: encodeScreen(screenWidth, screenHeight, colorDepth),
  };
}

function readCachedSignals() {
  const cached = sessionStorage.getItem(SIGNALS_KEY);
  if (!cached) return null;
  try {
    return JSON.parse(cached);
  } catch (e) {
    return null;
  }
}

function cacheSignals(signals) {
  sessionStorage.setItem(SIGNALS_KEY, JSON.stringify(signals));
  sessionStorage.setItem(FP_STORAGE_KEY, signals.fp8);
  sessionStorage.setItem(FP_READY_KEY, '1');
}

/**
 * 读取完整设备信号（session 级缓存）
 */
export function getDeviceSignals() {
  const cached = readCachedSignals();
  if (cached) {
    return cached;
  }
  const signals = buildDeviceSignals();
  cacheSignals(signals);
  return signals;
}

/**
 * App 启动时调用，采集并缓存设备指纹
 */
export function initDeviceFingerprint() {
  if (initPromise) {
    return initPromise;
  }

  initPromise = Promise.resolve().then(() => getDeviceSignals());
  return initPromise;
}

/**
 * 同步读取短指纹
 */
export function getFingerprintShort() {
  const cached = sessionStorage.getItem(FP_STORAGE_KEY);
  if (cached) {
    return cached;
  }
  const signals = getDeviceSignals();
  return signals.fp8;
}

function randomNonce(length = 2) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * 构建 v2 明文 Seal（<=53 字节，供 RSA 加密）
 * v2|fp8|ts6|n2|z3|s5|cp2|lg2|u4|v4|w4
 */
export function buildSealPlaintext(signals) {
  const s = signals || getDeviceSignals();
  const ts6 = Math.floor(Date.now() / 1000).toString(36).padStart(6, '0').slice(-6);
  const n2 = randomNonce(2);
  const z3 = (s.timezone + 840).toString(36).padStart(3, '0').slice(-3);
  const cp2 = `${Math.min(Math.max(s.cores, 0), 35).toString(36)}${s.platformCode}`;

  const plain = [
    'v2',
    s.fp8,
    ts6,
    n2,
    z3,
    s.screenCode,
    cp2,
    s.language.padEnd(2, 'x').slice(0, 2),
    s.ua4,
    s.cv4,
    s.wg4,
  ].join('|');

  if (plain.length > RSA_MAX_PLAIN_BYTES) {
    console.warn('[ClientSeal] plaintext exceeds RSA limit:', plain.length, plain);
  }

  return plain;
}

/**
 * 本地调试：解析 v2 明文（后端可按同样规则实现）
 */
export function parseSealPlaintext(plain) {
  const parts = plain.split('|');
  if (parts.length !== 11 || parts[0] !== 'v2') {
    throw new Error('Invalid v2 seal plaintext');
  }

  const screen = decodeScreen(parts[5]);
  const cp2 = parts[6];

  return {
    version: parts[0],
    fp8: parts[1],
    ts: parseInt(parts[2], 36),
    nonce: parts[3],
    timezone: parseInt(parts[4], 36) - 840,
    screenWidth: screen.screenWidth,
    screenHeight: screen.screenHeight,
    colorDepth: screen.colorDepth,
    cores: parseInt(cp2.slice(0, 1), 36) || 0,
    platformCode: cp2.slice(1, 2),
    platformLabel: PLATFORM_LABELS[cp2.slice(1, 2)] || 'Other',
    language: parts[7],
    ua4: parts[8],
    cv4: parts[9],
    wg4: parts[10],
  };
}

export { RSA_MAX_PLAIN_BYTES, PLATFORM_LABELS };
